export type AppSupportStatus =
  | "NEW"
  | "TRIAGED"
  | "IN_PROGRESS"
  | "WAITING_ON_USER"
  | "RESOLVED"
  | "CLOSED";

export type AppSupportSeverity = "LOW" | "NORMAL" | "HIGH" | "URGENT";

export type AppSupportCategory =
  | "LOGIN_ACCESS"
  | "CHAT"
  | "NOTIFICATIONS"
  | "UPLOAD"
  | "PERFORMANCE"
  | "DATA_DISPLAY"
  | "PERMISSION"
  | "FORM_SUBMISSION"
  | "OTHER";

export interface AppSupportTicket {
  ticket_id: number;
  business_unit: string;
  created_by: string;
  last_updated_by?: string;
  title: string;
  description?: string | null;
  category?: AppSupportCategory | null;
  severity: AppSupportSeverity;
  status: AppSupportStatus;
  source_page?: string | null;
  browser_info?: string | null;
  app_version?: string | null;
  company_id?: string | null;
  assigned_to?: string | null;
  resolution?: string | null;
  closed_at?: string | null;
  updated_at?: string;
  created_at?: string;
  // Joined fields
  created_by_name?: string | null;
  created_by_email?: string | null;
  assigned_to_name?: string | null;
  last_updated_by_name?: string | null;
  company_name_text?: string | null;
  comment_count?: number;
  attachments?: AppSupportAttachment[];
}

export interface AppSupportAttachment {
  attachment_id: string;
  s3_key: string;
  s3_bucket: string;
  name: string;
  type: string;
}

export interface AppSupportFormData {
  title: string;
  description?: string;
  category?: AppSupportCategory | null;
  severity?: AppSupportSeverity;
  source_page?: string;
  browser_info?: string;
  app_version?: string;
  company_id?: string | null;
}

export interface AppSupportUpdateData {
  title?: string;
  description?: string | null;
  category?: AppSupportCategory | null;
  severity?: AppSupportSeverity;
  source_page?: string | null;
  browser_info?: string | null;
  app_version?: string | null;
  // Support/admin fields
  status?: AppSupportStatus;
  assigned_to?: string | null;
  resolution?: string | null;
  closed_at?: string | null;
}

export interface AppSupportAgent {
  value: string;
  label: string;
}

export const STATUS_LABELS: Record<AppSupportStatus, string> = {
  NEW:             "app_support.status_new",
  TRIAGED:         "app_support.status_triaged",
  IN_PROGRESS:     "app_support.status_in_progress",
  WAITING_ON_USER: "app_support.status_waiting_on_user",
  RESOLVED:        "app_support.status_resolved",
  CLOSED:          "app_support.status_closed",
};

export const SEVERITY_LABELS: Record<AppSupportSeverity, string> = {
  LOW:    "app_support.severity_low",
  NORMAL: "app_support.severity_normal",
  HIGH:   "app_support.severity_high",
  URGENT: "app_support.severity_urgent",
};

export const CATEGORY_LABELS: Record<AppSupportCategory, string> = {
  LOGIN_ACCESS:    "app_support.category_login_access",
  CHAT:            "app_support.category_chat",
  NOTIFICATIONS:   "app_support.category_notifications",
  UPLOAD:          "app_support.category_upload",
  PERFORMANCE:     "app_support.category_performance",
  DATA_DISPLAY:    "app_support.category_data_display",
  PERMISSION:      "app_support.category_permission",
  FORM_SUBMISSION: "app_support.category_form_submission",
  OTHER:           "app_support.category_other",
};

export const SEVERITY_OPTIONS: { value: AppSupportSeverity; label: string }[] = [
  { value: "LOW",    label: "app_support.severity_low"    },
  { value: "NORMAL", label: "app_support.severity_normal" },
  { value: "HIGH",   label: "app_support.severity_high"   },
  { value: "URGENT", label: "app_support.severity_urgent" },
];

export const CATEGORY_OPTIONS: { value: AppSupportCategory; label: string }[] = [
  { value: "LOGIN_ACCESS",    label: "app_support.category_login_access"    },
  { value: "CHAT",            label: "app_support.category_chat"            },
  { value: "NOTIFICATIONS",   label: "app_support.category_notifications"   },
  { value: "UPLOAD",          label: "app_support.category_upload"          },
  { value: "PERFORMANCE",     label: "app_support.category_performance"     },
  { value: "DATA_DISPLAY",    label: "app_support.category_data_display"    },
  { value: "PERMISSION",      label: "app_support.category_permission"      },
  { value: "FORM_SUBMISSION", label: "app_support.category_form_submission" },
  { value: "OTHER",           label: "app_support.category_other"           },
];

export const STATUS_OPTIONS: { value: AppSupportStatus; label: string }[] = [
  { value: "NEW",             label: "app_support.status_new"             },
  { value: "TRIAGED",         label: "app_support.status_triaged"         },
  { value: "IN_PROGRESS",     label: "app_support.status_in_progress"     },
  { value: "WAITING_ON_USER", label: "app_support.status_waiting_on_user" },
  { value: "RESOLVED",        label: "app_support.status_resolved"        },
  { value: "CLOSED",          label: "app_support.status_closed"          },
];
